import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { PageHero, Loader } from "../components/ui-bits";
import { Reveal } from "../components/Reveal";
import { MediaImage } from "../components/MediaImage";

const BG = "https://images.unsplash.com/photo-1635751753205-e443f0bc4b02?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Verticals() {
  const [verticals, setVerticals] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/verticals").then((r) => setVerticals(r.data)).catch(() => setVerticals([]));
  }, []);

  const categories = useMemo(() => {
    if (!verticals) return [];
    return ["All", ...Array.from(new Set(verticals.map((v) => v.category)))];
  }, [verticals]);

  const filtered = useMemo(() => {
    if (!verticals) return [];
    return verticals.filter((v) => {
      const matchCat = cat === "All" || v.category === cat;
      const matchQ = !q || v.name.toLowerCase().includes(q.toLowerCase()) || (v.excerpt || "").toLowerCase().includes(q.toLowerCase());
      return matchCat && matchQ;
    });
  }, [verticals, q, cat]);

  if (verticals === null) return <Loader label="Loading verticals" />;

  return (
    <div data-testid="verticals-page">
      <PageHero
        eyebrow="Our Ecosystem"
        title="Eleven interconnected verticals. One global vision."
        subtitle="Each vertical is led by senior specialists, backed by international partnerships and built for a fast-changing global economy."
        bg={BG}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-4 mb-10">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              data-testid="vertical-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search verticals..."
              className="w-full bg-[#0D131F] border border-slate-800 rounded-full pl-11 pr-4 py-3 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500/50"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="vertical-category-filter">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`vertical-cat-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                  cat === c ? "bg-emerald-500 text-slate-950 border-emerald-500" : "border-slate-800 text-slate-300 hover:border-emerald-500/50"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="text-slate-500 py-20 text-center font-mono">No verticals match your search.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((v, i) => (
              <Reveal key={v.id} delay={(i % 3) * 0.06}>
                <Link
                  to={`/verticals/${v.slug}`}
                  data-testid={`vertical-card-${v.slug}`}
                  className="card-vertical rounded-2xl overflow-hidden group flex flex-col h-full"
                >
                  <div className="relative h-44 overflow-hidden">
                    <MediaImage src={v.image_url} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0D131F] via-transparent to-transparent" />
                    <span className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-widest text-emerald-300 bg-emerald-950/70 border border-emerald-500/30 rounded-full px-2.5 py-1">{v.category}</span>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-display font-semibold text-white text-lg group-hover:text-emerald-400 transition-colors">{v.name}</h3>
                    <p className="text-sm text-slate-400 mt-2 line-clamp-3 flex-1">{v.excerpt}</p>
                    <span className="text-emerald-400 text-sm font-mono mt-4">Learn more →</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
